import React, { useState, useEffect } from 'react';
import { Eye, Edit, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchVendorsBySalesExecutive } from '../../services/SalesExecutive/vendorOnboardingService';
import toast, { Toaster } from 'react-hot-toast';

const ROLE_NAMES = {
  1: "Hospital",
  2: "Clinic", 
  3: "Diagnostic Center",
  4: "Medical Store",
  5: "Ambulance Agency",
  6: "Blood Bank",
  8: "Product Partner"
};

const getUserId = () => {
  // Try to get from localStorage (as per your useAuth)
  return localStorage.getItem('userId');
};

const SalesExecutiveVendorOnboardingPage: React.FC = () => {
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleOnboardVendor = () => {
    navigate('/sales/vendor-onboarding/add');
  };

  const handleEditVendor = (vendor: any) => {
    const vendorRole = vendor.vendor.vendorRole;
    const category = ROLE_NAMES[vendorRole as keyof typeof ROLE_NAMES];
    
    // Navigate to the appropriate edit form based on vendor role
    switch (vendorRole) {
      case 1: // Hospital
        navigate(`/sales/vendor-onboarding/edit/hospital/${vendor.vendor.vendorId}`, { 
          state: { vendorData: vendor } 
        });
        break;
      case 2: // Clinic
        navigate(`/sales/vendor-onboarding/edit/clinic/${vendor.vendor.vendorId}`, { 
          state: { vendorData: vendor } 
        });
        break;
      case 3: // Diagnostic Center
        navigate(`/sales/vendor-onboarding/edit/diagnostic-center/${vendor.vendor.vendorId}`, { 
          state: { vendorData: vendor } 
        });
        break;
      case 4: // Medical Store
        navigate(`/sales/vendor-onboarding/edit/medical-store/${vendor.vendor.vendorId}`, { 
          state: { vendorData: vendor } 
        });
        break;
      case 5: // Ambulance Agency
        navigate(`/sales/vendor-onboarding/edit/ambulance/${vendor.vendor.vendorId}`, { 
          state: { vendorData: vendor } 
        });
        break;
      case 6: // Blood Bank
        navigate(`/sales/vendor-onboarding/edit/blood-bank/${vendor.vendor.vendorId}`, { 
          state: { vendorData: vendor } 
        });
        break;
      case 8: // Product Partner
        navigate(`/sales/vendor-onboarding/edit/product-partner/${vendor.vendor.vendorId}`, { 
          state: { vendorData: vendor } 
        });
        break;
      default:
        toast.error('Unknown vendor type');
        break;
    }
  };

  useEffect(() => {
    const fetchVendors = async () => {
      setLoading(true);
      setError(null);
      try {
        const userId = getUserId();
        if (!userId) {
          setError('User not logged in.');
          setLoading(false);
          return;
        }
        const res = await fetchVendorsBySalesExecutive(userId);
        setVendors(res.data.vendors || []);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to fetch vendors.');
      } finally {
        setLoading(false);
      }
    };
    fetchVendors();
  }, []);

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />
      <div className="flex items-center justify-between mb-2">
        <span className="text-base text-gray-600 dark:text-gray-300">Manage and onboard vendors below.</span>
        <button
          onClick={handleOnboardVendor}
          className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg shadow text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" /> Onboard Vendor
        </button>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-primary-400 dark:scrollbar-thumb-primary-700 scrollbar-track-gray-100 dark:scrollbar-track-gray-800">
        <table className="w-full min-w-[900px]">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Sr No</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Vendor Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Phone No</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date & Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">Loading...</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-red-500">{error}</td>
              </tr>
            ) : vendors.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">No vendors onboarded yet.</td>
              </tr>
            ) : (
              vendors.map((item, idx) => (
                <tr key={item.vendor.vendorId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{idx + 1}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{item.category}</td>
                  <td className="px-6 py-4 whitespace-nowrap flex items-center">
                    {/* No profile_image in API, fallback to initials */}
                    <div className="h-8 w-8 rounded-full bg-primary-200 dark:bg-primary-800 flex items-center justify-center mr-2 text-primary-700 dark:text-primary-200 font-bold">
                      {item.profile?.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <span className="text-gray-900 dark:text-white font-medium">{item.profile?.name || '-'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{item.vendor?.email || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{item.vendor?.phoneNumber || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      item.vendor?.status === 'approved'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                        : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                    }`}>
                      {item.vendor?.status || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.vendor?.createdAt ? new Date(item.vendor.createdAt).toLocaleString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex space-x-2">
                      <button className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors" title="View"><Eye className="w-4 h-4" /></button>
                      <button 
                        className="p-1 text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-300 transition-colors" 
                        title="Edit"
                        onClick={() => handleEditVendor(item)}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesExecutiveVendorOnboardingPage; 